import { useState } from "react";
import { Button } from "Components/Common/Button/Button";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { ReactComponent as BarsIcon } from "Assets/Bars.svg";
import { QUESTION_TYPES, conditionValueToText } from "./constants";
import { QuestionModal } from "./QuestionModal";

const typeLabel = (type) =>
  QUESTION_TYPES.find((entry) => entry.value === type)?.label || type;

const Chip = ({ className, children }) => (
  <span className={`px-2 py-0.5 text-xs rounded-full ${className}`}>{children}</span>
);

export const QuestionsTab = ({ questions, onChange }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null); // null = adding

  const onDragEnd = (result) => {
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }
    const reordered = [...questions];
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    onChange(reordered);
  };

  const saveQuestion = (question) => {
    const next = [...questions];
    if (editIndex === null) {
      next.push(question);
    } else {
      next[editIndex] = question;
    }
    onChange(next);
    setModalOpen(false);
  };

  const removeQuestion = (index) => {
    onChange(questions.filter((_, questionIndex) => questionIndex !== index));
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">
          One screen per question. "Show if" rules may only reference earlier
          questions — checked at publish.
        </p>
        <Button
          btnText="Add Question"
          className="h-9 px-3"
          onClick={() => {
            setEditIndex(null);
            setModalOpen(true);
          }}
        />
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="questions">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
              {questions.map((question, index) => (
                <Draggable key={question.key || index} draggableId={question.key || String(index)} index={index}>
                  {(draggableProvided) => (
                    <div
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                      className="bg-white border border-gray-200 rounded-lg p-3 flex items-start gap-3 shadow-sm"
                    >
                      <div {...draggableProvided.dragHandleProps} className="pt-1">
                        <BarsIcon className="h-4 w-4 text-gray-400 cursor-grab" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-mono text-gray-400">{index + 1}.</span>
                          <span className="text-sm font-medium text-gray-900">{question.title}</span>
                        </div>
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                          <Chip className="bg-blue-100 text-blue-800">{typeLabel(question.type)}</Chip>
                          <Chip className="bg-gray-100 text-gray-600 font-mono">{question.key}</Chip>
                          {question.required !== false && question.type !== "INFO" && (
                            <Chip className="bg-gray-100 text-gray-600">required</Chip>
                          )}
                          {question.showIf?.length > 0 && (
                            <Chip className="bg-purple-100 text-purple-800">
                              show if {question.showIf.map((condition) =>
                                `${condition.questionKey} ${condition.op} ${conditionValueToText(condition.value)}`
                              ).join(" AND ")}
                            </Chip>
                          )}
                          {question.disqualify && (
                            <Chip
                              className={
                                question.disqualify.action === "BLOCK"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }
                            >
                              {question.disqualify.action === "BLOCK" ? "blocks" : "warns"} on{" "}
                              {conditionValueToText(question.disqualify.value)}
                            </Chip>
                          )}
                          {question.options?.some((option) => option.disqualify) && (
                            <Chip className="bg-yellow-100 text-yellow-800">option disqualifiers</Chip>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          btnText="Edit"
                          variant="Secondary"
                          className="h-8 px-2"
                          onClick={() => {
                            setEditIndex(index);
                            setModalOpen(true);
                          }}
                        />
                        <Button
                          btnText="Remove"
                          variant="Delete"
                          className="h-8 px-2"
                          onClick={() => removeQuestion(index)}
                        />
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
              {questions.length === 0 && (
                <div className="text-center text-sm text-gray-500 border border-dashed border-gray-300 rounded-lg py-10">
                  No questions yet — this form will go straight to checkout.
                </div>
              )}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {modalOpen && (
        <QuestionModal
          open={modalOpen}
          setOpen={setModalOpen}
          question={editIndex !== null ? questions[editIndex] : null}
          earlierQuestions={
            editIndex !== null ? questions.slice(0, editIndex) : questions
          }
          existingKeys={questions
            .filter((_, questionIndex) => questionIndex !== editIndex)
            .map((question) => question.key)}
          onSave={saveQuestion}
        />
      )}
    </div>
  );
};
